// ============================================================================
// CrewAhead Maritime Platform - Taxonomy Cascade Helpers
// ============================================================================
// Shared utilities for cascading taxonomy selectors (e.g. vessel category ->
// vessel type, shore category -> shore position). Keeps hierarchy-aware logic
// in one place so consumers don't duplicate recursive walk logic.
// ============================================================================

import type { TaxonomyItem } from '@/lib/adapters';

/** Minimal shape needed for a category node in a hierarchy walk. */
export interface TaxonomyCategoryNode {
  id: number;
  parentCategory?: number | null | undefined;
}

/**
 * Return every `item` whose category field belongs to the selected category
 * or any of its descendant subcategories.
 *
 * The category tree is currently two levels deep in most places, but the
 * implementation walks recursively so deeper nesting works without changes.
 *
 * @param selectedCategoryId - The ID of the category selected by the user.
 * @param categories - All category taxonomy items. Must include the numeric
 *   `parentCategory` FK so descendant subcategories can be discovered.
 * @param items - All item taxonomy items to filter.
 * @param itemCategoryField - The property name on each item that holds the
 *   numeric category FK (e.g. `'vesselCategory'` or `'shoreCategory'`).
 */
export function getTaxonomyItemsForCategory<T, C extends TaxonomyCategoryNode>(
  selectedCategoryId: number,
  categories: C[],
  items: T[],
  itemCategoryField: keyof T
): T[] {
  const descendantIds = new Set<number>([selectedCategoryId]);
  let frontier = [selectedCategoryId];

  while (frontier.length) {
    const children = categories.filter((c) =>
      frontier.includes(c.parentCategory ?? -1)
    );
    frontier = children.map((c) => c.id);
    frontier.forEach((id) => descendantIds.add(id));
  }

  return items.filter((item) => {
    const value = item[itemCategoryField] as unknown;
    return typeof value === 'number' && descendantIds.has(value);
  });
}

/**
 * Return vessel types whose `vesselCategory` belongs to the selected vessel
 * category or any of its descendants.
 */
export function getVesselTypesForCategory(
  selectedCategoryId: number,
  vesselCategories: TaxonomyItem[],
  vesselTypes: TaxonomyItem[]
): TaxonomyItem[] {
  return getTaxonomyItemsForCategory(
    selectedCategoryId,
    vesselCategories,
    vesselTypes,
    'vesselCategory'
  );
}

/**
 * Return shore positions whose `shoreCategory` belongs to the selected shore
 * category or any of its descendants.
 */
export interface ShorePositionItem {
  id: number;
  positionId: string;
  positionName: string;
  level: string;
  shoreCategory: number | null;
}

export function getShorePositionsForCategory(
  selectedCategoryId: number,
  shoreCategories: TaxonomyItem[],
  shorePositions: ShorePositionItem[]
): ShorePositionItem[] {
  return getTaxonomyItemsForCategory(
    selectedCategoryId,
    shoreCategories,
    shorePositions,
    'shoreCategory'
  );
}
